import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = t('testimonials.items');

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#C9A84C]" />
            <span className="text-[#C9A84C] font-semibold text-sm tracking-wider uppercase">
              Social Proof
            </span>
            <div className="w-12 h-[2px] bg-[#C9A84C]" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#1A1F4B] mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-[#1F2937]/70 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="absolute -top-8 left-0 md:left-8">
            <Quote className="w-16 h-16 text-[#C9A84C]/20" />
          </div>

          <div className="bg-[#F9FAFB] p-8 md:p-12 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                data-testid="testimonial-content"
              >
                <p className="text-[#1A1F4B] text-lg md:text-xl leading-relaxed mb-8 italic">
                  "{testimonials[currentIndex].quote}"
                </p>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#1A1F4B] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {testimonials[currentIndex].name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1A1F4B]">
                      {testimonials[currentIndex].name}
                    </h4>
                    <p className="text-[#1F2937]/60 text-sm">
                      {testimonials[currentIndex].role}, {testimonials[currentIndex].company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="absolute bottom-8 right-8 flex items-center gap-2">
              <button
                onClick={prevTestimonial}
                data-testid="testimonial-prev"
                className="w-10 h-10 bg-[#1A1F4B] text-white flex items-center justify-center hover:bg-[#C9A84C] hover:text-[#1A1F4B] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextTestimonial}
                data-testid="testimonial-next"
                className="w-10 h-10 bg-[#1A1F4B] text-white flex items-center justify-center hover:bg-[#C9A84C] hover:text-[#1A1F4B] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="absolute bottom-8 left-8 flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-[#C9A84C]' : 'bg-[#1A1F4B]/30'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
